import { useState } from 'react'
import { format, parse, isValid } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface Props {
    id: string
    value: string
    onChange: (value: string) => void
    required?: boolean
    /** Earliest / latest selectable day, as yyyy-MM-dd. */
    min?: string
    max?: string
    invalid?: boolean
    placeholder?: string
}

const toDate = (v?: string) => {
    if (!v) return undefined
    const d = parse(v, 'yyyy-MM-dd', new Date())
    return isValid(d) ? d : undefined
}

export default function DateInput({ id, value, onChange, min, max, invalid, placeholder = 'Pick a date' }: Props) {
    const [open, setOpen] = useState(false)
    const parsed = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined
    const selected = parsed && isValid(parsed) ? parsed : undefined

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    id={id}
                    variant="outline"
                    aria-invalid={invalid || undefined}
                    className={cn('w-56 justify-start gap-2 font-normal', !selected && 'text-muted-foreground')}
                >
                    <CalendarIcon className="h-4 w-4" />
                    {selected ? format(selected, 'PPP') : placeholder}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={(d) => {
                        onChange(d ? format(d, 'yyyy-MM-dd') : '')
                        setOpen(false)
                    }}
                    disabled={[...(toDate(min) ? [{ before: toDate(min)! }] : []), ...(toDate(max) ? [{ after: toDate(max)! }] : [])]}
                    defaultMonth={selected}
                    autoFocus
                />
            </PopoverContent>
        </Popover>
    )
}
